/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { DateRange } from "react-day-picker";
import { useRouter, useSearchParams } from "next/navigation";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/useMobile";

interface DatePickerRangeFilterProps {
  containerClassName?: string;
  className?: string;
  placeholder?: string;
  label?: boolean;
}

export function DatePickerRangeFilter({
  placeholder = "Pilih Tanggal",
  className,
  containerClassName,
  label = true,
}: DatePickerRangeFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();

  const [date, setDate] = React.useState<DateRange>({
    from: searchParams.get("startDate")
      ? new Date(searchParams.get("startDate") as string)
      : undefined,
    to: searchParams.get("endDate")
      ? new Date(
          new Date(searchParams.get("endDate") as string).getTime() -
            24 * 60 * 60 * 1000
        )
      : undefined,
  });

  React.useEffect(() => {
    if (date?.from && date?.to) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("startDate", format(date.from, "yyyy-MM-dd"));
      params.set(
        "endDate",
        format(new Date(date.to.getTime() + 24 * 60 * 60 * 1000), "yyyy-MM-dd")
      );
      router.replace(`?${params.toString()}`, { scroll: false });
    }
  }, [date?.from, date?.to]);

  const handleDateSelect = (range: DateRange | undefined) => {
    setDate(range ?? { from: undefined, to: undefined });
  };

  const handleReset = () => {
    setDate({ from: undefined, to: undefined });
    const params = new URLSearchParams(searchParams.toString());
    params.delete("startDate");
    params.delete("endDate");
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  return (
    <div>
      {label && <div className="text-sm mb-1 font-medium">Tanggal</div>}
      <div className={cn("relative w-full", containerClassName)}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full rounded-full justify-between text-left font-normal overflow-hidden flex items-center h-[2.6rem] gap-2",
                className
              )}
            >
              <div className="truncate">
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "dd/MM/yyyy")} -{" "}
                      {format(date.to, "dd/MM/yyyy")}
                    </>
                  ) : (
                    format(date.from, "dd/MM/yyyy")
                  )
                ) : (
                  <span>{placeholder ?? "Pilih Rentang Tanggal"}</span>
                )}
              </div>

              <CalendarIcon
                size={20}
                color="#2F5673"
                className={cn("", date?.from && "absolute right-8")}
              />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              selected={date}
              onSelect={handleDateSelect}
              numberOfMonths={isMobile ? 1 : 2}
              captionLayout="dropdown"
            />
          </PopoverContent>
        </Popover>

        {/* Reset Button */}
        {date?.from && (
          <button
            onClick={handleReset}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
